import type { SerializedAssetShare } from 'fym-shared/src/music24/assetShares';

/**
 * What the share box shows (design a1 off, a2 on, a4 turned off, a5 loading):
 * - `loading`: the GET of the link runs.
 * - `off`: no live link.
 * - `on`: a live link.
 * - `turnedOff`: the user turned the link off in this view ("Share a new link").
 */
export type ShareBoxStatus = 'loading' | 'off' | 'on' | 'turnedOff';

/**
 * The error line of the share box: 400 `M24_ASSET_NOT_READY` (`notReady`), any other refused
 * POST (`failed`), a refused DELETE (`turnOffFailed`).
 */
export type ShareErrorKind = 'notReady' | 'failed' | 'turnOffFailed';

export interface ShareUiState {
  /** The "Turn off the link?" dialog is open (a3). */
  confirmOpen: boolean;
  /** The link of this asset was turned off in this view (a4). */
  turnedOff: boolean;
  error: ShareErrorKind | null;
}

export type ShareUiAction =
  | { type: 'createStarted' }
  | { type: 'createFailed'; error: ShareErrorKind }
  | { type: 'created' }
  | { type: 'askTurnOff' }
  | { type: 'cancelTurnOff' }
  | { type: 'turnedOff' }
  | { type: 'turnOffFailed' }
  | { type: 'reset' };

export const INITIAL_SHARE_UI: ShareUiState = { confirmOpen: false, turnedOff: false, error: null };

/** The local state of the share box of one asset. A move to another asset resets it. Pure. */
export function shareUiReducer(state: ShareUiState, action: ShareUiAction): ShareUiState {
  switch (action.type) {
    case 'createStarted':
      return { ...state, error: null };
    case 'createFailed':
      return { ...state, error: action.error };
    case 'created':
      return { ...state, error: null, turnedOff: false };
    case 'askTurnOff':
      return { ...state, confirmOpen: true };
    case 'cancelTurnOff':
      return { ...state, confirmOpen: false };
    case 'turnedOff':
      return { ...state, confirmOpen: false, turnedOff: true, error: null };
    case 'turnOffFailed':
      return { ...state, confirmOpen: false, error: 'turnOffFailed' };
    case 'reset':
      return INITIAL_SHARE_UI;
    default:
      return state;
  }
}

/** The status of the share box from the GET answer and the local state. Pure. */
export function shareBoxStatus(input: {
  isLoading: boolean;
  share: SerializedAssetShare | null | undefined;
  turnedOff: boolean;
}): ShareBoxStatus {
  if (input.share !== null && input.share !== undefined) {
    return 'on';
  }
  if (input.isLoading) {
    return 'loading';
  }
  return input.turnedOff ? 'turnedOff' : 'off';
}

/**
 * The kind of a refused POST. `customBaseQuery` puts the REST `cause` on the error as
 * `m24Limit`: 400 `{ code: 'M24_ASSET_NOT_READY' }` → `notReady`; everything else → `failed`.
 */
export function shareErrorKind(error: unknown): Exclude<ShareErrorKind, 'turnOffFailed'> {
  if (typeof error !== 'object' || error === null) {
    return 'failed';
  }
  const cause = (error as { m24Limit?: unknown }).m24Limit;
  if (typeof cause !== 'object' || cause === null) {
    return 'failed';
  }
  return (cause as { code?: unknown }).code === 'M24_ASSET_NOT_READY' ? 'notReady' : 'failed';
}
